'use client';

// [CS02] Maintaining organization with imports
import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { PotenzialDashboardMobile } from './PotenzialDashboardMobile';
import { PotentialDisplay } from '../molecular/PotentialDisplay';
import { PieChart } from '../molecular/PieChart';
import { ImpactCard } from '../molecular/ImpactCard';
import { EnergySourceCard } from '../molecular/EnergySourceCard';
import { ComparisonBar } from '../molecular/ComparisonBar';
import { CitizenParticipationCalculator } from '../molecular/CitizenParticipationCalculator';
import { CallToActionSection } from '../molecular/CallToActionSection';
import { ExportSection } from '../molecular/ExportSection';
import { InstallationsExplorer } from './InstallationsExplorer';
import { ScenarioCalculator } from './ScenarioCalculator';
import { HistoricDevelopmentChart } from './HistoricDevelopmentChart';
// import { Card } from '../atomic/Card';
import { BackButton } from '../atomic/BackButton';
import { UserFriendlyError } from '../atomic/UserFriendlyError';
import { RattenbergEasterEgg } from '../molecular/RattenbergEasterEgg';
import { DetailedLoadingState } from '../atomic/DetailedLoadingState';
import { AnimatedCard } from '../atomic/AnimatedCard';
import { FadeInSection } from '../atomic/FadeInSection';
import { InfoTooltip } from '../atomic/Tooltip';
// import { LoadingProgress } from '../atomic/LoadingProgress';
// import { ErrorMessage } from '../atomic/ErrorMessage';
// import { Leaf, Zap, Sun, Droplets } from 'lucide-react';
import { getEnergyDataByPLZ, calculateTotalEnergy, calculateProjected2025Energy, EnergyData, getEnergyDataByGemeinde, calculateTirolAverage, loadGemeindenData, loadGemeindenOnlineData, searchGemeindenOnline, convertOnlineToLegacyFormat, getIntegratedEnergyData, GemeindeOnlineData } from '@/services/energy';
import { neighborService } from '@/services/energy/molecular/neighborService';
import { analytics } from '@/utils/analytics';
// import { EnergyPlant } from '@/types/energy';

type Installation = {
  id: string;
  name: string;
  type: 'solar' | 'water' | 'biomass' | 'biogas';
  capacity: number;
  yearlyProduction: number;
};

// [TS01] Type safety with interface
interface PotenzialDashboardProps {
  gemeinde: string; // This is actually a PLZ
  onBack: () => void;
  onStatsCalculated?: (stats: { jahresPotenzial: number; anlagenGesamt: number; co2Ersparnis: number }) => void;
  onGemeindeInfoLoaded?: (info: { name: string }) => void;
  energySupplier?: 'ikb' | 'tiwag';
}

// [DC01] Main dashboard organic component that assembles all pieces
// Version identifier for this component
// const DASHBOARD_VERSION = '2.0.0-atomic-20240523';

// Log version on component mount
function useDashboardVersionLog() {
  useEffect(() => {
    // Version logging removed for production
    return () => {};
  }, []);
}

// Named export for better tree-shaking and explicit imports
export function PotenzialDashboard({ gemeinde, onBack, onStatsCalculated, onGemeindeInfoLoaded, energySupplier = 'ikb' }: PotenzialDashboardProps) {
  useDashboardVersionLog();
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState<boolean | null>(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // [EH01] Track loading and error states
  type LoadingState = 'idle' | 'loading' | 'success' | 'error';
  type ErrorType = 'network' | 'data' | 'not-found' | 'invalid' | 'unknown';
  
  const [loadingState, setLoadingState] = useState<LoadingState>('idle');
  const [error, setError] = useState<{ type: ErrorType; message: string } | null>(null);
  
  // Helper function to check if we're in a loading state
  const isLoading = loadingState === 'loading' || loadingState === 'idle';
  const [energyData, setEnergyData] = useState<EnergyData | null>(null);
  const [gemeindeInfo, setGemeindeInfo] = useState<{ name: string; einwohner: number; haushalte: number; flaeche: number; gemeindeNummer?: string } | null>(null);
  const [neighborComparison, setNeighborComparison] = useState<Array<{ name: string; value: number; distance?: number }>>([]);
  const [tirolAverage, setTirolAverage] = useState<number | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  
  // State for toggling between views
  const [showPieChart, setShowPieChart] = useState(false);
  const [showHistoricChart, setShowHistoricChart] = useState(true); // Show historic chart by default
  
  // [DM01] Scenario state for what-if calculations
  const [scenario, setScenario] = useState<{
    increasedSolar: number;
    increasedWind?: number;  // Keep for backward compatibility
    increasedBiomass: number;
    increasedBiogas: number;
    increasedHydro: number;
    einspeisungRate?: number;  // Optional for backward compatibility
  } | null>(null);
  
  // Scenario calculator callback functions
  const handleScenarioCalculate = useCallback((scenarioData: {
    increasedSolar: number;
    increasedBiogas: number;
    increasedBiomass: number;
    increasedHydro: number;
    einspeisungRate: number;
  }) => {
    setScenario(scenarioData);
    console.log('[PotenzialDashboard] Scenario updated:', scenarioData);
  }, []);
  
  const handleResetScenario = useCallback(() => {
    setScenario(null);
    console.log('[PotenzialDashboard] Scenario reset');
  }, []);

  // [EH03] Load real energy data when component mounts
  const loadEnergyData = useCallback(async () => {
    if (!gemeinde) return;
    
    setLoadingState('loading');
    setError(null);
    
    // Track start time to ensure minimum loading duration
    const startTime = Date.now();
    
    try {
      // Check if input is a PLZ (4 digits), gemeinde ID (5 digits), or a gemeinde name
      // This ensures Reith bei Kitzbühel (70414) gets its own data, not shared PLZ 6370 data
      const isPLZ = /^\d{4}$/.test(gemeinde);
      const isGemeindeId = /^\d{5}$/.test(gemeinde);
      
      console.log(`[PotenzialDashboard] Input analysis: "${gemeinde}" - isPLZ: ${isPLZ}, isGemeindeId: ${isGemeindeId}`);
      
      let result;
      if (isPLZ) {
        result = await getEnergyDataByPLZ(gemeinde);
      } else if (isGemeindeId) {
        // Get energy data by gemeinde ID
        const energyData = await getEnergyDataByGemeinde(gemeinde);
        
        // Load gemeinde info separately using new online data format
        const gemeindeOnlineData = await loadGemeindenOnlineData();
        const onlineData = gemeindeOnlineData[gemeinde];
        const gemeindeInfo = onlineData ? convertOnlineToLegacyFormat(gemeinde, onlineData) : null;
        
        result = { energyData, gemeindeInfo };
      } else {
        // Get energy data by gemeinde name
        const energyData = await getEnergyDataByGemeinde(gemeinde);
        
        // Load gemeinde info separately
        const gemeindeDataFile = await loadGemeindenData();
        const gemeindeInfo = gemeindeDataFile.gemeinden.find(g => 
          g.name.toLowerCase() === gemeinde.toLowerCase()
        ) || null;
        
        result = { energyData, gemeindeInfo };
      }
      // Check if we have valid data
      if (!result.energyData) {
        throw new Error('Keine Energiedaten verfügbar');
      }
      
      // Check if we have any energy plants
      const hasEnergyData = [
        result.energyData.sonne,
        result.energyData.wasser,
        result.energyData.biomasse,
        result.energyData.biogas
      ].some(plants => plants && plants.length > 0);
      
      if (!hasEnergyData) {
        const identifier = /^\d{4}$/.test(gemeinde) ? `PLZ ${gemeinde}` : gemeinde;
        throw { 
          message: `Keine Energieanlagen für ${identifier} gefunden`,
          type: 'not-found' as const
        };
      }
      
      // Set gemeinde info if available
      if (result.gemeindeInfo) {
        
        // Debug: Check flaeche data and wappen
        console.log('[PotenzialDashboard] Gemeinde Info:', {
          name: result.gemeindeInfo.name,
          flaecheKm2: result.gemeindeInfo.flaecheKm2,
          flaecheHa: result.gemeindeInfo.flaecheHa,
          wappenLink: (result.gemeindeInfo as any).wappenLink
        });

        const info = {
          name: result.gemeindeInfo.name,
          einwohner: result.gemeindeInfo.einwohner,
          haushalte: result.gemeindeInfo.haushalte,
          flaeche: result.gemeindeInfo.flaecheKm2 || (result.gemeindeInfo.flaecheHa ? result.gemeindeInfo.flaecheHa / 100 : 0), // Use flaecheKm2 directly if available
          gemeindeNummer: result.gemeindeInfo.gemeindeNummer,
          wappenLink: (result.gemeindeInfo as any).wappenLink // Add wappenLink to the info object
        };
        
        setGemeindeInfo(info);
        
        // Notify parent component
        if (onGemeindeInfoLoaded) {
          onGemeindeInfoLoaded({ 
            name: info.name,
            wappenLink: (info as any).wappenLink 
          });
        }
      }
      
      // Ensure minimum loading time of 3 seconds
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, 3000 - elapsedTime);
      
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
      
      // Set all data at once after the delay
      setEnergyData(result.energyData);
      setLastUpdated(new Date());
      setLoadingState('success');
      
      // Track successful energy analysis
      const totalEnergyProjection = calculateProjected2025Energy(result.energyData);
      const solarEnergy = result.energyData.sonne ? calculateProjected2025Energy({ sonne: result.energyData.sonne, wasser: [], biomasse: [], biogas: [] }) : 0;
      const waterEnergy = result.energyData.wasser ? calculateProjected2025Energy({ sonne: [], wasser: result.energyData.wasser, biomasse: [], biogas: [] }) : 0;
      const biomassEnergy = result.energyData.biomasse ? calculateProjected2025Energy({ sonne: [], wasser: [], biomasse: result.energyData.biomasse, biogas: [] }) : 0;
      const biogasEnergy = result.energyData.biogas ? calculateProjected2025Energy({ sonne: [], wasser: [], biomasse: [], biogas: result.energyData.biogas }) : 0;
      const totalEnergySum = solarEnergy + waterEnergy + biomassEnergy + biogasEnergy;
      
      analytics.trackEnergyAnalysis({
        municipality: result.gemeindeInfo?.name || gemeinde,
        postal_code: isPLZ ? gemeinde : result.gemeindeInfo?.plz || '',
        total_potential: totalEnergySum,
        pv_potential: solarEnergy,
        water_potential: waterEnergy,
        biomass_potential: biomassEnergy,
        biogas_potential: biogasEnergy,
        euro_savings: totalEnergySum * 0.106, // Standard rate
      });
    } catch (err) {
      console.error('[Dashboard] Error loading energy data:', err);
      
      // Use the error message from the service
      let errorType: ErrorType = 'unknown';
      let errorMessage = err instanceof Error ? err.message : 'Ein unerwarteter Fehler ist aufgetreten';
      
      // Determine error type based on message content
      if (err instanceof Error) {
        if (err.message.includes('Verbindungsfehler')) {
          errorType = 'network';
        } else if (err.message.includes('nicht gefunden') || err.message.includes('Keine Energieanlagen')) {
          errorType = 'not-found';
        } else if (err.message.includes('Ungültige PLZ')) {
          errorType = 'invalid';
        }
      } else if (typeof err === 'object' && err !== null && 'type' in err) {
        // Handle our custom error objects
        const customError = err as { type?: string; message?: string };
        if (customError.type === 'not-found') {
          errorType = 'not-found';
        }
        errorMessage = customError.message || errorMessage;
      }
      
      // Ensure minimum loading time even for errors for better UX
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, 1500 - elapsedTime); // 1.5 seconds for errors
      
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
      
      setError({
        message: errorMessage,
        type: errorType
      });
      
      // Track errors for analytics
      analytics.trackError(
        err instanceof Error ? err : new Error(errorMessage),
        `dashboard_load_${gemeinde}`
      );
      setLoadingState('error');
    }
  }, [gemeinde, onGemeindeInfoLoaded]);
  
  // Load data on mount and when gemeinde changes
  useEffect(() => {
    loadEnergyData();
  }, [loadEnergyData]);
  
  // [EH03] Load neighbor comparison data
  useEffect(() => {
    const loadNeighborData = async () => {
      if (!gemeindeInfo?.gemeindeNummer || !energyData) return;
      
      try {
        // Loading neighbor data...
        
        // Get neighbors
        const neighbors = await neighborService.getClosestNeighbors(gemeindeInfo.gemeindeNummer, 2);
        
        // Calculate neighbor potentials
        const neighborData = await Promise.all(
          neighbors.map(async (neighbor) => {
            try {
              const neighborEnergyData = await getEnergyDataByGemeinde(neighbor.iso);
              const totalNeighborErtrag = calculateProjected2025Energy(neighborEnergyData);
              const solarErtrag = neighborEnergyData.sonne ? calculateProjected2025Energy({ sonne: neighborEnergyData.sonne, wasser: [], biomasse: [], biogas: [] }) : 0;
              const wasserErtrag = neighborEnergyData.wasser ? calculateProjected2025Energy({ sonne: [], wasser: neighborEnergyData.wasser, biomasse: [], biogas: [] }) : 0;
              const biomasseErtrag = neighborEnergyData.biomasse ? calculateProjected2025Energy({ sonne: [], wasser: [], biomasse: neighborEnergyData.biomasse, biogas: [] }) : 0;
              const biogasErtrag = neighborEnergyData.biogas ? calculateProjected2025Energy({ sonne: [], wasser: [], biomasse: [], biogas: neighborEnergyData.biogas }) : 0;
              
              const totalErtrag = solarErtrag + wasserErtrag + biomasseErtrag + biogasErtrag;
              
              // Skip neighbors with no energy data
              if (totalErtrag === 0) {
                console.log(`No energy data for neighbor ${neighbor.name} (${neighbor.iso})`);
                return null;
              }
              
              const handelbareFaktor = 0.4;
              const preisProKwh = energySupplier === 'tiwag' ? 0.087 : 0.093; // EEG additional savings
              const potential = Math.round(totalErtrag * handelbareFaktor * preisProKwh);
              
              return {
                name: neighbor.name,
                value: potential,
                distance: neighbor.distance
              };
            } catch (error) {
              console.error(`Failed to load data for ${neighbor.name} (${neighbor.iso}):`, error);
              return null;
            }
          })
        );
        
        // Filter out failed loads and ensure type safety
        const validNeighborData = neighborData.filter(
          (d): d is { name: string; value: number; distance: number } => 
            d !== null && typeof d.distance === 'number'
        );
        setNeighborComparison(validNeighborData);
        
        // Get Tirol average if not already loaded
        if (tirolAverage === null) {
          // Calculating Tirol average...
          const average = await calculateTirolAverage();
          setTirolAverage(average);
        }
      } catch (error) {
        console.error('[Dashboard] Error loading neighbor data:', error);
      }
    };
    
    loadNeighborData();
  }, [gemeindeInfo, energyData, tirolAverage]);
  
  // [PF02] Memoize data transformations
  // [CS02] Define the dashboard data structure
  type DashboardData = {
    totalPotential: number;
    solarPotential: number;
    hydroPotential: number;
    biomassPotential: number;
    biogasPotential: number;
    energyDistribution: Array<{ type: string; percentage: number }>;
    installations: Installation[];
    jahresPotenzial: number;
    einwohner: number;
    haushalte: number;
    flaeche: number;
    energieQuellen: Array<{
      name: string;
      anzahl: number;
      kwh: number;
      color: string;
      potentialEuro: number;
    }>;
    vergleich: Array<{ name: string; value: number }>;
  };

  // Calculate total projected kWh for 2025 description using realistic chart logic
  const projected2025kWh = useMemo(() => {
    if (!energyData || !Object.keys(energyData).length) {
      return 2450000; // Fallback value
    }
    
    // Use EXACT value from HistoricDevelopmentChart cache (authoritative source)
    try {
      const chartCacheKey = `projection_2025_${gemeinde}`;
      const cachedStr = localStorage.getItem(chartCacheKey);
      
      if (cachedStr) {
        const cached = JSON.parse(cachedStr);
        const exactTotalPower = cached.solarPower + cached.waterPower + cached.biomassPower + cached.biogasPower;
        console.log(`[PotenzialDashboard] Using EXACT HistoricChart 2025 value for ${gemeinde}:`, exactTotalPower);
        return Math.round(exactTotalPower);
      }
    } catch (e) {
      console.warn('[PotenzialDashboard] Could not read HistoricChart cache');
    }
    
    // Only use fallback if HistoricChart hasn't calculated yet
    return calculateRealistic2025Projection(energyData);
  }, [energyData, gemeinde]);

  // Helper function to get 2025 projection from HistoricChart cache (authoritative source)
  function calculateRealistic2025Projection(energyData: EnergyData): number {
    const sonneData = energyData.sonne || [];
    const wasserData = energyData.wasser || [];
    const biomasseData = energyData.biomasse || [];
    const biogasData = energyData.biogas || [];
    
    // Try to find HistoricChart cached projection first (most accurate)
    try {
      const chartCacheKey = `projection_2025_${gemeinde}`;
      const cachedStr = localStorage.getItem(chartCacheKey);
      
      if (cachedStr) {
        const cached = JSON.parse(cachedStr);
        const totalPower = cached.solarPower + cached.waterPower + cached.biomassPower + cached.biogasPower;
        console.log(`[PotenzialDashboard] Using HistoricChart 2025 projection for ${gemeinde}:`, totalPower);
        return Math.round(totalPower);
      }
    } catch (e) {
      console.warn('[PotenzialDashboard] Could not read HistoricChart cache');
    }
    
    // Fallback: Calculate simple projection as backup
    const fallbackEnergyData = { sonne: sonneData, wasser: wasserData, biomasse: biomasseData, biogas: biogasData };
    const totalFallbackErtrag = calculateProjected2025Energy(fallbackEnergyData);
    const solarErtrag2024 = sonneData ? calculateProjected2025Energy({ sonne: sonneData, wasser: [], biomasse: [], biogas: [] }) : 0;
    const wasserErtrag2024 = wasserData ? calculateProjected2025Energy({ sonne: [], wasser: wasserData, biomasse: [], biogas: [] }) : 0;
    const biomasseErtrag2024 = biomasseData ? calculateProjected2025Energy({ sonne: [], wasser: [], biomasse: biomasseData, biogas: [] }) : 0;
    const biogasErtrag2024 = biogasData ? calculateProjected2025Energy({ sonne: [], wasser: [], biomasse: [], biogas: biogasData }) : 0;
    
    // Use conservative fallback growth rates if HistoricChart hasn't calculated yet
    const fallbackGrowthRates = { solar: 8, water: 0.5, biomass: 1, biogas: 1 };
    
    const totalProjected2025 = 
      solarErtrag2024 * (1 + fallbackGrowthRates.solar / 100) +
      wasserErtrag2024 * (1 + fallbackGrowthRates.water / 100) +
      biomasseErtrag2024 * (1 + fallbackGrowthRates.biomass / 100) +
      biogasErtrag2024 * (1 + fallbackGrowthRates.biogas / 100);
    
    console.log('[PotenzialDashboard] Using fallback 2025 projection (HistoricChart not cached yet):', Math.round(totalProjected2025));
    return Math.round(totalProjected2025);
  }

  // [PF02] Memoize data transformations
  const dashboardData = useMemo<DashboardData>(() => {
    // If real data is loaded, use it; otherwise fall back to mock data
    if (energyData && Object.keys(energyData).length > 0) {
      // Use 2025 projection values from HistoricChart cache (authoritative source)
      // This ensures consistency between Dashboard and Chart
      let solarErtrag, wasserErtrag, biomasseErtrag, biogasErtrag;
      
      try {
        // Look for cache specific to current gemeinde (PLZ)
        const chartCacheKey = `projection_2025_${gemeinde}`;
        const cachedStr = localStorage.getItem(chartCacheKey);
        
        if (cachedStr) {
          const cached = JSON.parse(cachedStr);
          solarErtrag = cached.solarPower;
          wasserErtrag = cached.waterPower;
          biomasseErtrag = cached.biomassPower;
          biogasErtrag = cached.biogasPower;
          console.log(`[PotenzialDashboard] Using HistoricChart cached 2025 values for ${gemeinde}:`, { solar: Math.round(solarErtrag), water: Math.round(wasserErtrag), biomass: Math.round(biomasseErtrag), biogas: Math.round(biogasErtrag) });
        } else {
          console.log(`[PotenzialDashboard] No cached 2025 projection found for ${gemeinde}, using fallback calculation`);
        }
      } catch (e) {
        console.warn('[PotenzialDashboard] Could not read HistoricChart cache for financial calculation');
      }
      
      // Fallback to current year values if cache not available
      if (!solarErtrag) {
        const fallbackTotal = calculateProjected2025Energy(energyData);
        solarErtrag = energyData.sonne ? calculateProjected2025Energy({ sonne: energyData.sonne, wasser: [], biomasse: [], biogas: [] }) : 0;
        wasserErtrag = energyData.wasser ? calculateProjected2025Energy({ sonne: [], wasser: energyData.wasser, biomasse: [], biogas: [] }) : 0;
        biomasseErtrag = energyData.biomasse ? calculateProjected2025Energy({ sonne: [], wasser: [], biomasse: energyData.biomasse, biogas: [] }) : 0;
        biogasErtrag = energyData.biogas ? calculateProjected2025Energy({ sonne: [], wasser: [], biomasse: [], biogas: energyData.biogas }) : 0;
        console.log('[PotenzialDashboard] Using fallback projected 2025 values (HistoricChart not cached yet)');
      }
      
      // Calculate financial potential based on energy supplier
      const handelbareFaktor = 0.4; // 40% der Energie ist handelbar
      const preisProKwh = energySupplier === 'tiwag' ? 0.087 : 0.093; // EEG additional savings: TIWAG=€0.087, IKB=€0.093
      const solarPotential = Math.round(solarErtrag * handelbareFaktor * preisProKwh);
      const hydroPotential = Math.round(wasserErtrag * handelbareFaktor * preisProKwh);
      const biomassPotential = Math.round(biomasseErtrag * handelbareFaktor * preisProKwh);
      const biogasPotential = Math.round(biogasErtrag * handelbareFaktor * preisProKwh);
      const totalPotential = solarPotential + hydroPotential + biomassPotential + biogasPotential;
      
      // Compare with projected2025kWh for troubleshooting
      const calculatedTotal = Math.round(solarErtrag + wasserErtrag + biomasseErtrag + biogasErtrag);
      const projectedTotal = projected2025kWh;
      const deviation = Math.abs(calculatedTotal - projectedTotal);
      const deviationPercent = projectedTotal > 0 ? (deviation / projectedTotal) * 100 : 0;
      
      console.log('[PotenzialDashboard] ✅ Using HistoricChart 2025 Values ✅:', {
        source: 'HistoricChart localStorage cache',
        projected2025: { solar: Math.round(solarErtrag), water: Math.round(wasserErtrag), biomass: Math.round(biomasseErtrag), biogas: Math.round(biogasErtrag) },
        potentials: { solar: solarPotential, water: hydroPotential, biomass: biomassPotential, biogas: biogasPotential, total: totalPotential },
        consistency: 'Dashboard and Chart now use same values'
      });
      
      console.log('[PotenzialDashboard] 🔍 VALUE COMPARISON TROUBLESHOOTING:', {
        calculatedTotal: calculatedTotal.toLocaleString(),
        projectedTotal: projectedTotal.toLocaleString(),
        deviation: deviation.toLocaleString(),
        deviationPercent: deviationPercent.toFixed(2) + '%',
        match: deviation === 0 ? '✅ EXACT MATCH' : '❌ MISMATCH'
      });
      
      // Transform energy plants to installation format and deduplicate by ID
      const seenIds = new Set<string>();
      const uniquePlants = {
        sonne: (energyData.sonne || []).filter(plant => {
          if (!plant.id || seenIds.has(plant.id)) return false;
          seenIds.add(plant.id);
          return true;
        }),
        wasser: (energyData.wasser || []).filter(plant => {
          if (!plant.id || seenIds.has(plant.id)) return false;
          seenIds.add(plant.id);
          return true;
        }),
        biomasse: (energyData.biomasse || []).filter(plant => {
          if (!plant.id || seenIds.has(plant.id)) return false;
          seenIds.add(plant.id);
          return true;
        }),
        biogas: (energyData.biogas || []).filter(plant => {
          if (!plant.id || seenIds.has(plant.id)) return false;
          seenIds.add(plant.id);
          return true;
        })
      };
      
      const installations = [
        ...uniquePlants.sonne.map(plant => ({
          id: plant.id,
          name: plant.name || 'Unbenannte Solaranlage',
          type: 'solar' as const,
          capacity: plant.leistungKw || 0,
          yearlyProduction: plant.ertragKwh || 0
        })),
        ...uniquePlants.wasser.map(plant => ({
          id: plant.id,
          name: plant.name || 'Unbenanntes Wasserkraftwerk',
          type: 'water' as const,
          capacity: plant.leistungKw || 0,
          yearlyProduction: plant.ertragKwh || 0
        })),
        ...uniquePlants.biomasse.map(plant => ({
          id: plant.id,
          name: plant.name || 'Unbenannte Biomasseanlage',
          type: 'biomass' as const,
          capacity: plant.leistungKw || 0,
          yearlyProduction: plant.ertragKwh || 0
        })),
        ...uniquePlants.biogas.map(plant => ({
          id: plant.id,
          name: plant.name || 'Unbenannte Biogasanlage',
          type: 'biogas' as const,
          capacity: plant.leistungKw || 0,
          yearlyProduction: plant.ertragKwh || 0
        }))
      ];
      
      // Sort installations by production capacity (descending)
      installations.sort((a, b) => b.yearlyProduction - a.yearlyProduction);
      
      return {
        totalPotential,
        solarPotential,
        hydroPotential,
        biomassPotential,
        biogasPotential,
        energyDistribution: [
          { type: 'solar', percentage: totalPotential > 0 ? Math.round((solarPotential / totalPotential) * 100) : 0 },
          { type: 'hydro', percentage: totalPotential > 0 ? Math.round((hydroPotential / totalPotential) * 100) : 0 },
          { type: 'biomass', percentage: totalPotential > 0 ? Math.round((biomassPotential / totalPotential) * 100) : 0 },
          { type: 'biogas', percentage: totalPotential > 0 ? Math.round((biogasPotential / totalPotential) * 100) : 0 }
        ],
        installations,
        jahresPotenzial: totalPotential, // Use HistoricChart-based calculation for consistency
        einwohner: gemeindeInfo?.einwohner || 15642,
        haushalte: gemeindeInfo?.haushalte || 7421,
        flaeche: gemeindeInfo?.flaeche || 45.8,
        energieQuellen: [
          { 
            name: 'SOLAR', 
            anzahl: energyData.sonne.length, 
            kwh: Math.round(solarErtrag), // 2025 projected value
            color: 'bg-yellow-400', 
            potentialEuro: solarPotential 
          },
          { 
            name: 'WASSERKRAFT', 
            anzahl: energyData.wasser.length, 
            kwh: Math.round(wasserErtrag), // 2025 projected value
            color: 'bg-blue-500', 
            potentialEuro: hydroPotential 
          },
          { 
            name: 'BIOMASSE', 
            anzahl: energyData.biomasse.length,
            kwh: Math.round(biomasseErtrag), // 2025 projected value
            color: 'bg-purple-500', 
            potentialEuro: biomassPotential 
          },
          { 
            name: 'BIOGAS', 
            anzahl: energyData.biogas.length, 
            kwh: Math.round(biogasErtrag), // 2025 projected value
            color: 'bg-pink-500', 
            potentialEuro: biogasPotential 
          }
        ],
        vergleich: [
          { name: gemeindeInfo?.name || `PLZ ${gemeinde}`, value: totalPotential },
          ...neighborComparison,
          ...(tirolAverage !== null ? [{ name: 'Ø Tirol', value: tirolAverage }] : [])
        ]
      };
    }
    
    // Fallback mock data if no real data is available
    return {
      totalPotential: 360000, // in €
      solarPotential: 144000, // 40%
      biogasPotential: 72000,  // 20%
      biomassPotential: 72000, // 20%
      hydroPotential: 72000,   // 20%,
      energyDistribution: [
        { type: 'solar', percentage: 40 },
        { type: 'biogas', percentage: 20 },
        { type: 'biomass', percentage: 20 },
        { type: 'hydro', percentage: 20 },
      ],
      installations: ([
        {
          id: '1',
          name: 'Gemeinde-Photovoltaikanlage',
          type: 'solar' as const,
          capacity: 250,
          yearlyProduction: 230000
        },
        {
          id: '2',
          name: 'Wasserkraftwerk Ost',
          type: 'water' as const,
          capacity: 500,
          yearlyProduction: 1200000
        },
        {
          id: '3',
          name: 'Familie Huber',
          type: 'solar' as const,
          capacity: 15,
          yearlyProduction: 13500
        },
        {
          id: '4',
          name: 'Kleinwasserkraftwerk Mühlbach',
          type: 'water' as const,
          capacity: 450,
          yearlyProduction: 980000
        },
        {
          id: '5',
          name: 'Bauernhof Maier',
          type: 'biomass' as const,
          capacity: 120,
          yearlyProduction: 420000
        },
        {
          id: '6',
          name: 'Schule Telfs',
          type: 'solar' as const,
          capacity: 68,
          yearlyProduction: 62000
        },
        {
          id: '7',
          name: 'Biogasanlage West',
          type: 'biogas' as const,
          capacity: 200,
          yearlyProduction: 1500000
        }
      ] as const) as Installation[],
      jahresPotenzial: 360000,
      einwohner: 15642,
      haushalte: 7421,
      flaeche: 45.8, // in km²
      energieQuellen: [
        { name: 'SOLAR', anzahl: 324, kwh: 7586804, color: 'bg-yellow-400', potentialEuro: 321600 },
        { name: 'WASSERKRAFT', anzahl: 3, kwh: 1200000, color: 'bg-blue-500', potentialEuro: 50880 },
        { name: 'BIOMASSE', anzahl: 0, kwh: 0, color: 'bg-green-500', potentialEuro: 0 },
      ],
      vergleich: [
        { name: gemeindeInfo?.name || `PLZ ${gemeinde}`, value: 360000 },
        { name: 'Nachbar 1', value: 142000 },
        { name: 'Nachbar 2', value: 128000 },
        { name: 'Ø Tirol', value: 85000 },
      ],
    };
  }, [energyData, gemeinde, gemeindeInfo, neighborComparison, tirolAverage]);

  // Emit stats when calculated
  useEffect(() => {
    if (dashboardData && energyData && onStatsCalculated && Array.isArray(dashboardData.energieQuellen)) {
      // Calculate total installations
      const anlagenGesamt = dashboardData.energieQuellen.reduce((sum, source) => sum + source.anzahl, 0);
      
      // Calculate CO2 savings (approx 0.4 kg CO2 per kWh saved)
      const totalKwh = dashboardData.energieQuellen.reduce((sum, source) => sum + source.kwh, 0);
      const co2Ersparnis = Math.round((totalKwh * 0.4) / 1000); // Convert to tons
      
      onStatsCalculated({
        jahresPotenzial: dashboardData.jahresPotenzial,
        anlagenGesamt,
        co2Ersparnis
      });
    }
  }, [dashboardData, energyData, onStatsCalculated]);


  // [CP01] Calculate values once rather than repeatedly
  const maxEnergieQuelle = Array.isArray(dashboardData.energieQuellen) 
    ? Math.max(...dashboardData.energieQuellen.map((q) => q.kwh)) 
    : 0;
  const maxVergleich = Array.isArray(dashboardData.vergleich) 
    ? Math.max(...dashboardData.vergleich.map((v) => v.value)) 
    : 0;
  const totalInstallations = Array.isArray(dashboardData.energieQuellen) 
    ? dashboardData.energieQuellen.reduce((sum: number, q) => sum + q.anzahl, 0) 
    : 0;

  // Calculate total potential based on scenario
  const totalPotential = useMemo(() => {
    if (!scenario) return dashboardData.totalPotential;
    
    // Apply Einspeisung rate adjustment (default 40% -> new rate)
    const einspeisungMultiplier = (scenario.einspeisungRate || 40) / 40;
    const adjustedBasePotential = dashboardData.totalPotential * einspeisungMultiplier;
    
    // Base potential for new installations (when current is 0)
    const baseBiogasPotential = 25000; // €25k for a new biogas plant
    const baseBiomassPotential = 20000; // €20k for a new biomass plant
    
    // Get current potentials
    const solarPotential = dashboardData.energieQuellen.find(s => s.name === 'SOLAR')?.potentialEuro || 0;
    const biomassPotential = dashboardData.energieQuellen.find(s => s.name === 'BIOMASSE')?.potentialEuro || 0;
    const biogasPotential = dashboardData.energieQuellen.find(s => s.name === 'BIOGAS')?.potentialEuro || 0;
    const hydroPotential = dashboardData.energieQuellen.find(s => s.name === 'WASSERKRAFT')?.potentialEuro || 0;
    
    // Calculate adjustments based on scenario (also apply Einspeisung rate)
    const solarAdjustment = solarPotential * einspeisungMultiplier * (scenario.increasedSolar / 100);
    const hydroAdjustment = hydroPotential * einspeisungMultiplier * ((scenario.increasedHydro || 0) / 100);
    
    // For Biogas/Biomasse: use existing potential if available, otherwise use base potential ONLY if increase > 0
    const biomassAdjustment = biomassPotential > 0
      ? biomassPotential * einspeisungMultiplier * (scenario.increasedBiomass / 100)
      : scenario.increasedBiomass > 0 ? baseBiomassPotential * einspeisungMultiplier * (scenario.increasedBiomass / 100) : 0;
      
    const biogasAdjustment = biogasPotential > 0
      ? biogasPotential * einspeisungMultiplier * (scenario.increasedBiogas / 100)
      : scenario.increasedBiogas > 0 ? baseBiogasPotential * einspeisungMultiplier * (scenario.increasedBiogas / 100) : 0;
    
    return Math.round(adjustedBasePotential + solarAdjustment + biomassAdjustment + biogasAdjustment + hydroAdjustment);
  }, [scenario, dashboardData]);
  
  // [PF02] Memoize data transformations for energy sources
  type EnergySource = {
    name: string;
    anzahl: number;
    kwh: number;
    color: string;
    potentialEuro: number;
  };

  const energySources = useMemo<EnergySource[]>(() => {
    if (!scenario) return dashboardData.energieQuellen as EnergySource[];
    
    // Apply Einspeisung rate adjustment
    const einspeisungMultiplier = (scenario.einspeisungRate || 40) / 40;
    
    // Base values for new installations
    const baseBiogasPotential = 25000;
    const baseBiomassPotential = 20000;
    const baseBiogasKwh = 1500000; // Typical biogas plant kWh
    const baseBiomassKwh = 1200000; // Typical biomass plant kWh
    
    if (!Array.isArray(dashboardData.energieQuellen)) return [];
    
    return (dashboardData.energieQuellen as EnergySource[]).map((source) => {
      let sourceMultiplier = 1;
      let additionalKwh = 0;
      let additionalPotential = 0;
      
      if (source.name === 'SOLAR') {
        sourceMultiplier = 1 + (scenario.increasedSolar / 100);
      } else if (source.name === 'WASSERKRAFT') {
        sourceMultiplier = 1 + ((scenario.increasedHydro || 0) / 100);
      } else if (source.name === 'BIOMASSE') {
        if (source.potentialEuro > 0) {
          sourceMultiplier = 1 + (scenario.increasedBiomass / 100);
        } else if (scenario.increasedBiomass > 0) {
          // Add new biomass installations ONLY if increase > 0
          additionalKwh = (baseBiomassKwh * scenario.increasedBiomass) / 100;
          additionalPotential = (baseBiomassPotential * einspeisungMultiplier * scenario.increasedBiomass) / 100;
        }
      } else if (source.name === 'BIOGAS') {
        if (source.potentialEuro > 0) {
          sourceMultiplier = 1 + (scenario.increasedBiogas / 100);
        } else if (scenario.increasedBiogas > 0) {
          // Add new biogas installations ONLY if increase > 0
          additionalKwh = (baseBiogasKwh * scenario.increasedBiogas) / 100;
          additionalPotential = (baseBiogasPotential * einspeisungMultiplier * scenario.increasedBiogas) / 100;
        }
      }
      
      // Apply both Einspeisung rate and source multiplier to existing installations
      const existingPotential = source.potentialEuro * einspeisungMultiplier * sourceMultiplier;
      const totalPotential = existingPotential + additionalPotential;
      
      return {
        ...source,
        kwh: Math.round(source.kwh * sourceMultiplier + additionalKwh),
        potentialEuro: Math.round(totalPotential)
      };
    });
  }, [scenario, dashboardData.energieQuellen]);
  
  
  // [EH03] Show loading state
  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <BackButton onClick={onBack} className="mb-6" />
        <DetailedLoadingState 
          gemeindeName={gemeindeInfo?.name}
          plz={gemeinde}
        />
      </div>
    );
  }

  // [EH02] Show error message if there was an error
  if (loadingState === 'error' && error) {
    // Special easter egg for Rattenberg (PLZ 6240 or Gemeinde-ID 70521)
    if ((gemeinde === '6240' || gemeinde === '70521') && error.type === 'not-found') {
      return <RattenbergEasterEgg onBack={onBack} />;
    }
    
    return (
      <div className="container mx-auto px-4 py-8">
        <BackButton onClick={onBack} className="mb-6" />
        <div className="max-w-2xl mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
          <UserFriendlyError
            title={error.type === 'not-found' ? 'Keine Daten verfügbar' : undefined}
            message={error.message}
            type={error.type as 'network' | 'not-found' | 'invalid' | 'unknown'}
            onRetry={() => loadEnergyData()}
            onGoHome={onBack}
          />
        </div>
      </div>
    );
  }

  // Don't render until we know if it's mobile or not (avoids hydration mismatch)
  if (isMobile === null) {
    return null;
  }

  // Use mobile version for screens smaller than 768px
  if (isMobile) {
    // Calculate installations count for mobile
    const anlagenCount = dashboardData?.energieQuellen.reduce((sum, source) => sum + source.anzahl, 0) || 0;
    const co2Savings = dashboardData ? Math.round((dashboardData.energieQuellen.reduce((sum, source) => sum + source.kwh, 0) * 0.4) / 1000) : 0;
    
    return (
      <PotenzialDashboardMobile
        gemeinde={gemeinde}
        onBack={onBack}
        onStatsCalculated={onStatsCalculated}
        onGemeindeInfoLoaded={onGemeindeInfoLoaded}
        energySupplier={energySupplier}
        energyData={energyData}
        gemeindeInfo={gemeindeInfo}
        stats={{
          jahresPotenzial: calculateProjected2025Energy(energyData || { sonne: [], wasser: [], biomasse: [], biogas: [] }) * 0.4 * 0.106,
          anlagenGesamt: anlagenCount,
          co2Ersparnis: co2Savings
        }}
        isLoading={isLoading}
        error={error}
      />
    );
  }

  // Main dashboard UI (Desktop)
  return (
    <div className="w-full max-w-[2000px] mx-auto px-4 py-6">
        <BackButton onClick={onBack} className="mb-4" />
        
        <div className="mb-6">
          <h2 className="text-sm md:text-base font-normal text-gray-600 dark:text-gray-400 mb-1">
            Öffentliche Energiegemeinschaften
          </h2>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
            Einsparungspotenzial für {gemeindeInfo?.name || `PLZ ${gemeinde}`}
          </h1>
          {gemeindeInfo?.einwohner && (
            <p className="text-gray-600 dark:text-gray-300">
              {gemeindeInfo.einwohner.toLocaleString('de-AT')} Einwohner • {gemeindeInfo.haushalte.toLocaleString('de-AT')} Haushalte • {gemeindeInfo.flaeche.toFixed(2)} km²
            </p>
          )}
        </div>

      {/* Top Row - Key Metrics - Three Separate Cards in One Row */}
      <div className="w-full mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          
          {/* Column 1: Main Potential */}
          <div>
            <PotentialDisplay 
              value={totalPotential} 
              title="Jährliches Potenzial"
              description={`Basierend auf realistischer Prognose von ${projected2025kWh.toLocaleString('de-AT')} kWh Einspeisung in 2025`}
              className="h-full"
              wappenLink={(gemeindeInfo as any)?.wappenLink}
              gemeindeName={gemeindeInfo?.name}
            />
          </div>
          
          {/* Column 2: Installations Summary */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-4 sm:p-6">
            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-3">
              Anlagenübersicht
              <InfoTooltip 
                content={{
                  title: "Erneuerbare Energieanlagen",
                  description: "Übersicht aller registrierten erneuerbaren Energieanlagen in Ihrer Gemeinde, basierend auf E-Control Daten."
                }}
              />
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between pb-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400">Gesamt:</span>
                <span className="font-medium">{totalInstallations} Anlagen</span>
              </div>
              {energySources.map((source, index) => (
                <div key={source.name} className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">{source.name}:</span>
                  <span className="font-medium">{source.anzahl}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Column 3: Quick Comparison */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-4 sm:p-6">
            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-3">
              Vergleich
              <InfoTooltip 
                content={{
                  title: "Gemeindevergleich",
                  description: "Vergleich des jährlichen Energiepotenzials mit benachbarten Gemeinden und dem Tiroler Durchschnitt."
                }}
              />
            </h3>
            <div className="space-y-2">
              {dashboardData.vergleich.slice(0, 3).map((item) => (
                <div key={item.name} className="flex justify-between text-sm">
                  <span className={`${item.name === gemeinde ? 'font-semibold text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'} truncate pr-2`}>
                    {item.name}
                  </span>
                  <span className="font-medium whitespace-nowrap">
                    €{item.value.toLocaleString('de-AT')}
                  </span>
                </div>
              ))}
            </div>
          </div>
          
        </div>
      </div>

      {/* Middle Row - Chart and Energy Sources */}
      <div className="flex flex-col lg:flex-row gap-6 mb-6">
        {/* Historic Development Chart - Full width on mobile, 2/3 on lg */}
        <div className="flex-1 lg:flex-[2] min-w-0 bg-white dark:bg-gray-800 rounded-xl shadow p-4 sm:p-6">
          <div className="flex justify-between items-center mb-3 sm:mb-4">
            <h2 className="text-base sm:text-lg font-semibold text-gray-700 dark:text-gray-200">
              Entwicklung Erneuerbarer Energien
              <InfoTooltip 
                content={{
                  title: "Historische Entwicklung",
                  description: "Zeigt die Entwicklung der erneuerbaren Energieanlagen von 2020 bis heute mit einer Prognose für 2025."
                }}
              />
            </h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => { setShowPieChart(false); setShowHistoricChart(false); }}
                className={`px-2 py-1 text-xs font-medium rounded-md ${!showPieChart && !showHistoricChart ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'}`}
              >
                Liste
              </button>
              <button
                onClick={() => { setShowPieChart(true); setShowHistoricChart(false); }}
                className={`px-2 py-1 text-xs font-medium rounded-md ${showPieChart ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'}`}
              >
                Verteilung
              </button>
              <button
                onClick={() => { setShowPieChart(false); setShowHistoricChart(true); }}
                className={`px-2 py-1 text-xs font-medium rounded-md ${showHistoricChart ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'}`}
              >
                Entwicklung
              </button>
            </div>
          </div>
          
          {showHistoricChart ? (
            <div className="h-[300px] sm:h-[350px] lg:h-[300px]">
              <HistoricDevelopmentChart 
                className="h-full"
                showTrendLine={true}
                showProjections={true}
                scenarioData={scenario}
                energyData={energyData}
                gemeinde={gemeinde}
              />
            </div>
          ) : showPieChart ? (
            <div className="h-[300px] sm:h-[350px] lg:h-[300px] -mt-2 -mx-2">
              <PieChart 
                data={energySources.map(source => ({
                  name: source.name,
                  value: source.kwh,
                  color: source.color.startsWith('#') ? source.color : 
                        source.name.toLowerCase().includes('solar') ? '#eab308' :
                        source.name.toLowerCase().includes('wasser') ? '#3b82f6' :
                        source.name.toLowerCase().includes('biomasse') ? '#16a34a' :
                        source.name.toLowerCase().includes('biogas') ? '#4ade80' :
                        '#60a5fa' // Default blue for others
                }))}
                compact={true}
                tooltip={true}
                legend={true}
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3">
              {energySources.map((source) => (
                <EnergySourceCard
                  key={source.name}
                  name={source.name}
                  installations={source.anzahl}
                  kwhPerYear={source.kwh}
                  potentialEuro={source.potentialEuro}
                  maxKwh={maxEnergieQuelle}
                  color={source.color}
                  compact={true}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Bottom Row - Comparison and Scenario */}
      <div className="flex flex-col lg:flex-row gap-6 mb-6">
        {/* Comparison Widget - Full width on mobile, 2/3 on lg */}
        <div className="flex-1 lg:flex-[2] min-w-0 bg-white dark:bg-gray-800 rounded-xl shadow p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-gray-800 dark:text-white">
            Vergleich mit Nachbargemeinden
            <InfoTooltip 
              content={{
                title: "Regionale Analyse",
                description: "Vergleich des Energiepotenzials mit angrenzenden Gemeinden zur Identifikation von Kooperationsmöglichkeiten für gemeinsame Energiegemeinschaften."
              }}
            />
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3">
            {dashboardData.vergleich.map((item) => (
              <ComparisonBar
                key={item.name}
                name={item.name}
                value={item.value}
                maxValue={maxVergleich}
                isSelected={item.name === gemeinde}
                compact={true}
              />
            ))}
          </div>
        </div>
        
        {/* Scenario Calculator - Full width on mobile, 1/3 on lg */}
        <div className="flex-1 min-w-0 bg-white dark:bg-gray-800 rounded-xl shadow p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-gray-800 dark:text-white">
            Szenario erstellen
            <InfoTooltip 
              content={{
                title: "Zukunftssimulation",
                description: "Simulieren Sie das Potenzial durch zusätzliche erneuerbare Energieanlagen und optimierte Einspeisequoten."
              }}
            />
          </h2>
          <ScenarioCalculator 
            currentPotential={dashboardData.energieQuellen.reduce((sum: number, q) => sum + q.potentialEuro, 0)}
            energySources={dashboardData.energieQuellen}
            onScenarioCalculate={handleScenarioCalculate}
            onReset={handleResetScenario}
            compact={true}
          />
        </div>
      </div>

      
      {/* Impact and Installations Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Impact Card - Takes 1/3 on large screens */}
        <div className="lg:col-span-1">
          <ImpactCard 
            annualSavings={energySources.reduce((sum, source) => sum + source.potentialEuro, 0)}
            annualProductionKwh={energySources.reduce((sum, source) => sum + source.kwh, 0)}
            compact={false}
            className="h-full"
          />
        </div>
        
        {/* Installations Explorer - Takes 2/3 on large screens */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-gray-800 dark:text-white">
            Anlagenübersicht
            <InfoTooltip 
              content={{
                title: "Detaillierte Anlagenliste",
                description: "Navigieren Sie durch alle erneuerbaren Energieanlagen in Ihrer Gemeinde mit detaillierten Leistungsdaten."
              }}
            />
          </h2>
          <InstallationsExplorer 
            installations={dashboardData.installations} 
            className="h-full"
          />
        </div>
      </div>


      {/* Citizen Participation & Export Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Citizen Participation Calculator */}
        <div>
          <CitizenParticipationCalculator
            haushalte={dashboardData.haushalte}
            gemeindeName={gemeindeInfo?.name || `PLZ ${gemeinde}`}
            jahresPotenzial={dashboardData.jahresPotenzial}
            energieProduktion={{
              solar: energyData?.sonne ? calculateProjected2025Energy({ sonne: energyData.sonne, wasser: [], biomasse: [], biogas: [] }) : 0,
              wasser: energyData?.wasser ? calculateProjected2025Energy({ sonne: [], wasser: energyData.wasser, biomasse: [], biogas: [] }) : 0,
              biomasse: energyData?.biomasse ? calculateProjected2025Energy({ sonne: [], wasser: [], biomasse: energyData.biomasse, biogas: [] }) : 0,
              biogas: energyData?.biogas ? calculateProjected2025Energy({ sonne: [], wasser: [], biomasse: [], biogas: energyData.biogas }) : 0
            }}
            energySupplier={energySupplier}
          />
        </div>

        {/* Export Section */}
        <div>
          <ExportSection
            gemeindeName={gemeindeInfo?.name || `PLZ ${gemeinde}`}
            gemeindeId={gemeindeInfo?.gemeindeNummer}
            wappenUrl={(gemeindeInfo as any)?.wappenLink}
            data={{
              jahresPotenzial: dashboardData.jahresPotenzial,
              einwohner: dashboardData.einwohner,
              haushalte: dashboardData.haushalte,
              energieQuellen: dashboardData.energieQuellen
            }}
          />
        </div>
      </div>

      {/* Call to Action */}
      <div className="mb-6">
        <CallToActionSection gemeindeName={gemeindeInfo?.name} />
      </div>
    </div>
  );
}
